import { useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useUnifiedValues } from "@/components/unified/UnifiedValuesContext";
import { FieldRenderer } from "@/components/filler/FieldRenderer";
import { FieldDefEditor } from "@/components/unified/FieldDefEditor";
import type { ContentField } from "@/types/template";
import { parseFieldDef } from "@/lib/parseFieldDef";

function initCompactState(raw: string, values: Record<string, unknown>): boolean {
  const f = parseFieldDef(raw);
  if (!f || (f.type !== "select" && f.type !== "score")) {
    return false;
  }
  const v = values[f.id];
  return v !== undefined && v !== "";
}

interface FieldGutterProps {
  editing: boolean;
  setEditing: (fn: (p: boolean) => boolean) => void;
  isCompactable: boolean;
  compact: boolean;
  setCompact: (fn: (p: boolean) => boolean) => void;
  isHidden: boolean;
  onToggleField: (id: string) => void;
  fieldId: string;
}

function FieldGutter(g: Readonly<FieldGutterProps>) {
  return (
    <div className="unified-field-gutter">
      <button
        tabIndex={-1}
        className={`unified-field-gutter-btn edit ${g.editing ? "active" : ""}`}
        title={g.editing ? "Cancel edit" : "Edit field definition"}
        onClick={() => {
          g.setEditing((p) => !p);
        }}
      >
        ✏
      </button>
      {g.isCompactable && !g.editing && (
        <button
          tabIndex={-1}
          className={`unified-field-gutter-btn compact-toggle ${g.compact ? "active" : ""}`}
          title={g.compact ? "Expand" : "Compact"}
          onClick={() => {
            g.setCompact((p) => !p);
          }}
        >
          ⊟
        </button>
      )}
      <button
        tabIndex={-1}
        className={`unified-field-gutter-btn field-visibility-toggle ${g.isHidden ? "active" : ""}`}
        title={g.isHidden ? "Show in report" : "Hide from report"}
        onClick={() => {
          g.onToggleField(g.fieldId);
        }}
      >
        👁
      </button>
    </div>
  );
}

function getSectionVisibility(sectionId: string | null, ctx: ReturnType<typeof useUnifiedValues>) {
  const sectionVisible = sectionId ? (ctx.sectionToggles[sectionId] ?? true) : true;
  const sectionCollapsed = sectionId ? (ctx.collapsedSections[sectionId] ?? false) : false;
  return { sectionCollapsed, sectionVisible };
}

interface FieldContentProps {
  editing: boolean;
  setEditing: (v: boolean) => void;
  field: ContentField;
  ctx: ReturnType<typeof useUnifiedValues>;
  compact: boolean;
  handleChange: (value: unknown) => void;
  updateAttributes: NodeViewProps["updateAttributes"];
}

function FieldContent({
  compact,
  ctx,
  editing,
  field,
  handleChange,
  setEditing,
  updateAttributes,
}: Readonly<FieldContentProps>) {
  if (editing) {
    return (
      <FieldDefEditor
        field={field}
        onSave={(updated) => {
          updateAttributes({ fieldDef: JSON.stringify(updated) });
          setEditing(false);
        }}
        onCancel={() => {
          setEditing(false);
        }}
      />
    );
  }
  return (
    <FieldRenderer
      field={field}
      value={ctx.values[field.id]}
      subTemplateData={ctx.subTemplateValues[field.id]}
      onChange={handleChange}
      onSubTemplateChange={(data) => {
        ctx.setSubTemplateValue(field.id, data);
      }}
      interpolate={ctx.interpolate}
      compact={compact}
    />
  );
}

function makeChangeHandler(
  fieldId: string,
  isCompactable: boolean,
  ctx: ReturnType<typeof useUnifiedValues>,
  setCompact: (v: boolean) => void,
) {
  return (value: unknown) => {
    ctx.setFieldValue(fieldId, value);
    if (isCompactable) {
      if (value !== undefined && value !== "") {
        setTimeout(() => {
          setCompact(true);
        }, 150);
      } else {
        setCompact(false);
      }
    }
  };
}

function InvalidFieldView() {
  return (
    <NodeViewWrapper>
      <div style={{ color: "red", fontSize: "12px", padding: "8px" }}>Invalid field</div>
    </NodeViewWrapper>
  );
}

export function FieldNodeView({ node, updateAttributes }: Readonly<NodeViewProps>) {
  const ctx = useUnifiedValues();
  const [editing, setEditing] = useState(false);
  const [compact, setCompact] = useState(() =>
    initCompactState(node.attrs.fieldDef as string, ctx.values),
  );

  const field = parseFieldDef(node.attrs.fieldDef as string);
  if (!field) {
    return <InvalidFieldView />;
  }

  const fieldId = field.id;
  const isCompactable = field.type === "select" || field.type === "score";
  const isHidden = ctx.fieldToggles[fieldId] === false;
  const { sectionCollapsed, sectionVisible } = getSectionVisibility(
    node.attrs.sectionId as string | null,
    ctx,
  );
  const handleChange = makeChangeHandler(fieldId, isCompactable, ctx, setCompact);

  return (
    <NodeViewWrapper
      className={`unified-field-node ${editing ? "is-editing" : ""} ${isHidden ? "is-hidden" : ""}`}
      style={sectionVisible && !sectionCollapsed ? undefined : { display: "none" }}
    >
      <FieldGutter
        editing={editing}
        setEditing={setEditing}
        isCompactable={isCompactable}
        compact={compact}
        setCompact={setCompact}
        isHidden={isHidden}
        onToggleField={ctx.onToggleField}
        fieldId={fieldId}
      />
      <div className="unified-field-content">
        <FieldContent
          editing={editing}
          setEditing={setEditing}
          field={field}
          ctx={ctx}
          compact={compact}
          handleChange={handleChange}
          updateAttributes={updateAttributes}
        />
      </div>
    </NodeViewWrapper>
  );
}
